/**
 * 处理中实时层：状态行、思考（可折叠）、命令输出（流式）、打字指示
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useChatStore } from '@/stores';
import { ansiToHtml } from '@/utils/ansiToHtml';

export const AgentLivePanel: React.FC = () => {
  const isProcessing = useChatStore((s) => s.isProcessing);
  const liveStatus = useChatStore((s) => s.liveStatus);
  const currentThinking = useChatStore((s) => s.currentThinking);
  const liveCommandOutput = useChatStore((s) => s.liveCommandOutput);
  const currentCommand = useChatStore((s) => s.currentCommand);
  const commandOutputDone = useChatStore((s) => s.commandOutputDone);
  const streamingAnswer = useChatStore((s) => s.streamingAnswer);
  const backgroundTasks = useChatStore((s) => s.backgroundTasks);
  const currentOperation = useChatStore((s) => s.currentOperation);
  const abortChatFn = useChatStore((s) => s.abortChatFn);

  const [thinkingOpen, setThinkingOpen] = useState(true);
  const [outputOpen, setOutputOpen] = useState(true);
  const [tasksOpen, setTasksOpen] = useState(true);
  const outputEndRef = useRef<HTMLDivElement>(null);

  const hasThinking = Boolean(currentThinking && currentThinking.trim());
  const showTyping = isProcessing && !hasThinking;
  const hasStatus = Boolean(liveStatus && liveStatus.trim());
  const hasOutput = Boolean(liveCommandOutput && liveCommandOutput.trim());
  const hasCommand = Boolean(currentCommand && currentCommand.trim());
  const hasStreamingAnswer = Boolean(streamingAnswer && streamingAnswer.trim());
  const visibleTasks = backgroundTasks.filter((t) =>
    t.status === 'running' || t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled'
  );
  const runningTasks = visibleTasks.filter((t) => t.status === 'running');
  const hasRunningTasks = runningTasks.length > 0;
  const hasVisibleTasks = visibleTasks.length > 0;
  const hasOperation = Boolean(currentOperation?.operationId && currentOperation.phase);
  const phaseLabelMap: Record<string, string> = {
    planning: '规划中',
    editing: '修复中',
    building: '构建中',
    fixing: '分析失败并修复中',
    verifying: '验证中',
    paused_step_budget: '单回合步数暂停',
    blocked: '等待人工确认',
    done: '已完成',
  };

  // D5: 后台任务输出渲染 — 每个 task 的 outputTail 转 HTML（尾部窗口，避免卡浏览器）
  const bgTaskOutputHtml = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of visibleTasks) {
      if (t.outputTail && t.outputTail.trim()) {
        map[t.taskId] = ansiToHtml(t.outputTail);
      }
    }
    return map;
  }, [visibleTasks]);

  // D5: 后台任务输出自动滚动
  const bgOutputEndRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    for (const t of visibleTasks) {
      const ref = bgOutputEndRefs.current[t.taskId];
      if (ref && tasksOpen) {
        ref.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [bgTaskOutputHtml, tasksOpen]);

  // 终端输出尾部窗口：只渲染最后 500 行，避免超长输出卡死浏览器
  const MAX_RENDER_LINES = 500;
  const { truncatedHtml } = useMemo(() => {
    const lines = liveCommandOutput ? liveCommandOutput.split('\n') : [];
    const total = lines.length;
    const text =
      total > MAX_RENDER_LINES
        ? `...[已省略前 ${total - MAX_RENDER_LINES} 行，以下为末尾 ${MAX_RENDER_LINES} 行]\n` +
          lines.slice(-MAX_RENDER_LINES).join('\n')
        : liveCommandOutput || '';
    return { truncatedHtml: ansiToHtml(text) };
  }, [liveCommandOutput]);

  // 自动滚动输出到底部（仅执行中）
  useEffect(() => {
    if (outputOpen && isProcessing && outputEndRef.current) {
      outputEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveCommandOutput, outputOpen, isProcessing]);

  // 流式答案变化时滚动到底部
  const answerEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hasStreamingAnswer && answerEndRef.current) {
      answerEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingAnswer, hasStreamingAnswer]);

  if (!isProcessing && !hasThinking && !hasStatus && !hasOutput && !hasCommand && !hasStreamingAnswer && !hasVisibleTasks && !hasOperation) {
    return null;
  }

  const outputDoneClass = commandOutputDone ? ' sse-command-output-done' : '';

  return (
    <div className="sse-agent-live-panel" aria-live="polite">
      {hasStatus && (
        <div className="sse-agent-live-meta">
          <span className="sse-agent-live-status">{liveStatus}</span>
          {isProcessing && abortChatFn && (
            <button
              type="button"
              className="sse-agent-abort-btn"
              onClick={abortChatFn}
              title="停止执行"
            >
              ■
            </button>
          )}
        </div>
      )}

      {hasOperation && currentOperation && (
        <div className="sse-operation-card">
          <div className="sse-operation-row">
            <span className="sse-operation-label">运维任务</span>
            <span className="sse-operation-phase">
              {phaseLabelMap[currentOperation.phase || ''] || currentOperation.phase}
            </span>
            {currentOperation.pendingResume && (
              <span className="sse-operation-badge">待恢复</span>
            )}
            {currentOperation.autoResumeCount ? (
              <span className="sse-operation-badge">自动恢复 {currentOperation.autoResumeCount}/5</span>
            ) : null}
          </div>
          {currentOperation.objective && (
            <div className="sse-operation-objective">
              {currentOperation.objective.substring(0, 160)}
            </div>
          )}
          {currentOperation.currentCommand && (
            <div className="sse-operation-command">
              <span>$</span> {currentOperation.currentCommand}
            </div>
          )}
          {currentOperation.lastObservation?.errorSummary && (
            <div className="sse-operation-error-wrap">
              <div className="sse-operation-error-title">最近失败摘要</div>
              <pre className="sse-operation-error">
                {currentOperation.lastObservation.errorSummary.substring(0, 2000)}
              </pre>
            </div>
          )}
        </div>
      )}

      {hasThinking && (
        <div className="sse-thinking-cursor">
          <button
            type="button"
            className="sse-thinking-cursor-toggle"
            onClick={() => setThinkingOpen((o) => !o)}
            aria-expanded={thinkingOpen}
          >
            <span className="sse-thinking-cursor-chevron">{thinkingOpen ? '▼' : '▶'}</span>
            <span className="sse-thinking-cursor-label">思考</span>
            {isProcessing && <span className="sse-thinking-cursor-pulse" />}
          </button>
          {thinkingOpen && (
            <pre className="sse-thinking-cursor-body">{currentThinking}</pre>
          )}
        </div>
      )}

      {hasVisibleTasks && (
        <div className="sse-bg-tasks-section">
          <button
            type="button"
            className="sse-bg-tasks-toggle"
            onClick={() => setTasksOpen((o) => !o)}
            aria-expanded={tasksOpen}
          >
            <span className="sse-bg-tasks-chevron">{tasksOpen ? '▼' : '▶'}</span>
            <span className="sse-bg-tasks-label">
              后台任务（{runningTasks.length} 个运行中）
            </span>
            {hasRunningTasks && <span className="sse-bg-tasks-pulse" />}
          </button>
          {tasksOpen && (
            <div className="sse-bg-tasks-list">
              {visibleTasks.map((task) => {
                const durationSec = task.duration
                  ? Math.round(task.duration / 1000)
                  : task.startTime
                    ? Math.round((Date.now() - task.startTime) / 1000)
                    : 0;
                const statusIcon =
                  task.status === 'completed' ? '✓' :
                  task.status === 'failed' ? '✗' : '◐';
                const outputHtml = bgTaskOutputHtml[task.taskId];
                return (
                  <div key={task.taskId} className="sse-bg-task-item">
                    <div className="sse-bg-task-header">
                      <span className="sse-bg-task-icon">{statusIcon}</span>
                      <span className="sse-bg-task-name">
                        {task.name || (task.command || '').substring(0, 50) || task.taskId}
                      </span>
                      <span className="sse-bg-task-meta">
                        {durationSec}s
                        {task.pollCount != null && task.pollCount > 0 ? ` · 轮询${task.pollCount}` : ''}
                      </span>
                    </div>
                    {/* D5: 后台任务实时终端输出 */}
                    {outputHtml && (
                      <div className="sse-bg-task-terminal">
                        <pre
                          className="sse-command-output-content"
                          dangerouslySetInnerHTML={{ __html: outputHtml }}
                        />
                        <div ref={(el) => { bgOutputEndRefs.current[task.taskId] = el; }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(hasCommand || hasOutput) && (
        <div className={`sse-command-output-section${outputDoneClass}`}>
          <button
            type="button"
            className="sse-command-output-toggle"
            onClick={() => setOutputOpen((o) => !o)}
            aria-expanded={outputOpen}
          >
            <span className="sse-command-output-chevron">{outputOpen ? '▼' : '▶'}</span>
            <span className="sse-command-output-label">
              {commandOutputDone ? '终端输出（已完成）' : '终端输出'}
            </span>
            {isProcessing && <span className="sse-command-output-pulse" />}
          </button>
          {outputOpen && (
            <div className="sse-command-output-box">
              {hasCommand && (
                <div className="sse-command-output-cmd">
                  <span className="sse-cmd-prompt">$</span> {currentCommand}
                </div>
              )}
              {hasOutput && (
                <pre
                  className="sse-command-output-content"
                  dangerouslySetInnerHTML={{ __html: truncatedHtml }}
                />
              )}
              <div ref={outputEndRef} />
            </div>
          )}
        </div>
      )}

      {showTyping && (
        <div className="sse-ai-typing sse-agent-typing" aria-hidden>
          <span />
          <span />
          <span />
        </div>
      )}

      {hasStreamingAnswer && (
        <div className="sse-streaming-answer" aria-live="polite">
          <div className="sse-streaming-answer-body">{streamingAnswer}</div>
          <div ref={answerEndRef} />
        </div>
      )}
    </div>
  );
};
